<template>
  <div
    class="customer-panel"
    :class="{'customer-panel--form-mode': activeTab === 'new'}"
  >
    <div class="customer-panel__inner">
    <div class="customer-panel__search">
      <i class="icon_search" aria-hidden="true"></i>
      <input
        v-model="customerName"
        type="text"
        class="customer-panel__search-input"
        placeholder="Search by name, NIC, passport, or mobile"
        autocomplete="off"
        @input="onSearchInput"
      />
    </div>

    <div class="customer-panel__tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="customer-panel__tab"
        :class="{'customer-panel__tab--active': activeTab === 'list'}"
        :aria-selected="activeTab === 'list'"
        @click="activeTab = 'list'"
      >
        <i class="icon_profile" aria-hidden="true"></i>
        Customer list
      </button>
      <button
        type="button"
        role="tab"
        class="customer-panel__tab"
        :class="{'customer-panel__tab--active': activeTab === 'new'}"
        :aria-selected="activeTab === 'new'"
        @click="activeTab = 'new'"
      >
        <i class="icon_plus" aria-hidden="true"></i>
        New customer
      </button>
    </div>
    </div>

    <div
      v-show="activeTab === 'list'"
      class="customer-panel__body customer-panel__body--list"
      role="tabpanel"
    >
      <div v-if="hasCustomers" class="customer-list">
        <article
          v-for="customer in Customers"
          :key="customerKey(customer)"
          class="customer-card"
        >
          <div class="customer-card__avatar" aria-hidden="true">
            <i class="icon_profile"></i>
          </div>
          <div class="customer-card__info">
            <h4 class="customer-card__name">{{ customer.name }}</h4>
            <p v-if="customer.address" class="customer-card__meta">
              <i class="icon_pin_alt" aria-hidden="true"></i>
              {{ customer.address }}
            </p>
            <p v-if="customer.phone" class="customer-card__meta">
              <i class="icon_phone" aria-hidden="true"></i>
              {{ customer.phone }}
            </p>
          </div>
          <button
            type="button"
            class="customer-card__select"
            @click="SelectCustomer(customer)"
          >
            Select
          </button>
        </article>
      </div>
      <p v-else class="customer-panel__empty">
        No customers found. Try another search or add a new customer.
      </p>
    </div>

    <div
      v-show="activeTab === 'new'"
      class="customer-panel__body customer-panel__body--form"
      role="tabpanel"
    >
      <form class="customer-form" @submit.prevent="CreateCustomer">
        <section class="customer-form__section">
          <h3 class="customer-form__heading">
            <i class="icon_id" aria-hidden="true"></i>
            General information
          </h3>
          <div class="customer-form__grid">
            <div class="customer-form__field customer-form__field--full">
              <label class="customer-form__label" for="guest-name">Full name</label>
              <input
                id="guest-name"
                type="text"
                class="customer-form__input"
                :class="{'customer-form__input--error': shouldShowError('name')}"
                placeholder="Customer name"
                name="name"
                v-model="Guest.name"
                @blur="touch('name')"
              />
              <small v-if="shouldShowError('name')" class="customer-form__error">
                {{ fieldErrors.name }}
              </small>
            </div>

            <div class="customer-form__field">
              <label class="customer-form__label" for="guest-passport">Passport</label>
              <input
                id="guest-passport"
                type="text"
                class="customer-form__input"
                :class="{'customer-form__input--error': shouldShowError('passport_number')}"
                placeholder="Passport number"
                name="passport_number"
                v-model="Guest.passport_number"
                @blur="touch('passport_number')"
              />
              <small
                v-if="shouldShowError('passport_number')"
                class="customer-form__error"
              >
                {{ fieldErrors.passport_number }}
              </small>
              <p
                v-if="duplicateInfo.passport_number && shouldShowError('passport_number')"
                class="customer-form__hint customer-form__hint--warn"
              >
                Existing: {{ duplicateInfo.passport_number.name }} /
                {{ duplicateInfo.passport_number.phone }}
                <span v-if="duplicateInfo.passport_number.address">
                  — {{ duplicateInfo.passport_number.address }}
                </span>
              </p>
            </div>

            <div class="customer-form__field">
              <label class="customer-form__label" for="guest-nic">NIC</label>
              <input
                id="guest-nic"
                type="text"
                class="customer-form__input"
                :class="{'customer-form__input--error': shouldShowError('nic_number')}"
                placeholder="NIC number"
                name="nic_number"
                v-model="Guest.nic_number"
                @blur="touch('nic_number')"
              />
              <small v-if="shouldShowError('nic_number')" class="customer-form__error">
                {{ fieldErrors.nic_number }}
              </small>
              <p
                v-if="duplicateInfo.nic_number && shouldShowError('nic_number')"
                class="customer-form__hint customer-form__hint--warn"
              >
                Existing: {{ duplicateInfo.nic_number.name }} /
                {{ duplicateInfo.nic_number.phone }}
                <span v-if="duplicateInfo.nic_number.address">
                  — {{ duplicateInfo.nic_number.address }}
                </span>
              </p>
            </div>
          </div>
        </section>

        <section class="customer-form__section">
          <h3 class="customer-form__heading">
            <i class="icon_mail_alt" aria-hidden="true"></i>
            Contact details
          </h3>
          <div class="customer-form__grid">
            <div class="customer-form__field customer-form__field--full">
              <label class="customer-form__label" for="guest-address">Address</label>
              <input
                id="guest-address"
                type="text"
                class="customer-form__input"
                placeholder="Address"
                name="address"
                v-model="Guest.address"
              />
            </div>

            <div class="customer-form__field">
              <label class="customer-form__label" for="guest-phone">Phone</label>
              <input
                id="guest-phone"
                type="tel"
                class="customer-form__input"
                :class="{'customer-form__input--error': shouldShowError('phone')}"
                placeholder="Phone number"
                name="phone"
                v-model="Guest.phone"
                @blur="touch('phone')"
              />
              <small v-if="shouldShowError('phone')" class="customer-form__error">
                {{ fieldErrors.phone }}
              </small>
              <p
                v-if="duplicateInfo.phone && shouldShowError('phone')"
                class="customer-form__hint customer-form__hint--warn"
              >
                Existing: {{ duplicateInfo.phone.name }} / {{ duplicateInfo.phone.phone }}
                <span v-if="duplicateInfo.phone.address">
                  — {{ duplicateInfo.phone.address }}
                </span>
              </p>
            </div>

            <div class="customer-form__field">
              <label class="customer-form__label" for="guest-email">Email</label>
              <input
                id="guest-email"
                type="email"
                class="customer-form__input"
                placeholder="Email address"
                name="email_address"
                v-model="Guest.email"
              />
            </div>
          </div>
        </section>

        <div class="customer-form__actions">
          <button
            type="submit"
            class="customer-form__submit"
            :disabled="creatingCustomer"
          >
            {{ creatingCustomer ? "Checking…" : "Create customer" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "@/plugins/axios";

export default {
  name: "SearchCustomer",
  data: () => ({
    activeTab: "list",
    Guest: {
      name: "",
      passport_number: "",
      nic_number: "",
      address: "",
      phone: "",
      email: "",
    },
    customerName: "",
    Customers: [],
    totalCustomers: 0,
    creatingCustomer: false,
    forceShowCustomerErrors: false,
    fieldErrors: {
      name: "",
      phone: "",
      nic_number: "",
      passport_number: "",
    },
    duplicateInfo: {
      phone: null,
      nic_number: null,
      passport_number: null,
    },
    touched: {
      name: false,
      phone: false,
      nic_number: false,
      passport_number: false,
    },
    _searchDebounceTimer: null,
  }),
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    selectedCustomer: {
      type: Object,
      default: () => ({}),
    },
    selectedPlace: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    hasCustomers() {
      return Array.isArray(this.Customers) && this.Customers.length > 0;
    },
  },
  methods: {
    customerKey(customer) {
      return (
        customer.id ||
        customer.phone ||
        customer.nic_number ||
        customer.passport_number ||
        customer.name
      );
    },

    touch(field) {
      if (this.touched && Object.prototype.hasOwnProperty.call(this.touched, field)) {
        this.touched[field] = true;
      }
      this.validateCustomerFields();
    },

    shouldShowError(field) {
      const hasError = !!(this.fieldErrors && this.fieldErrors[field]);
      const isTouched = !!(this.touched && this.touched[field]);
      return hasError && (isTouched || this.forceShowCustomerErrors);
    },

    validateCustomerFields() {
      const errors = {
        name: "",
        phone: "",
        nic_number: "",
        passport_number: "",
      };

      this.duplicateInfo = {
        phone: null,
        nic_number: null,
        passport_number: null,
      };

      if (!this.Guest?.name || String(this.Guest.name).trim() === "") {
        errors.name = "Customer name is required";
      }

      const phone = this.normalizePhone(this.Guest?.phone);
      if (!phone) {
        errors.phone = "Phone number is required";
      }

      if (this.Guest?.nic_number && this.normalizeDoc(this.Guest.nic_number) === "") {
        errors.nic_number = "NIC number is invalid";
      }
      if (
        this.Guest?.passport_number &&
        this.normalizeDoc(this.Guest.passport_number) === ""
      ) {
        errors.passport_number = "Passport number is invalid";
      }

      this.fieldErrors = { ...this.fieldErrors, ...errors };
      return errors;
    },

    hasCustomerErrors(errors) {
      return Object.values(errors || {}).some(v => !!v);
    },

    onSearchInput() {
      if (this._searchDebounceTimer) clearTimeout(this._searchDebounceTimer);
      this._searchDebounceTimer = setTimeout(() => {
        this.SearchCustomer(this.customerName);
      }, 350);
    },

    normalizePhone(value) {
      if (!value) return "";
      return String(value).replace(/[^\d]/g, "");
    },

    normalizeDoc(value) {
      if (!value) return "";
      return String(value).trim().toUpperCase().replace(/\s+/g, "");
    },

    async fetchGuestsByQuery(query) {
      const q = (query ?? "").toString().trim();
      const url = `hms/web/guests/search?query=${encodeURIComponent(q)}`;
      const res = await axios.get(url);
      return Array.isArray(res.data?.data) ? res.data.data : [];
    },

    async findDuplicateGuest(guest) {
      const phone = this.normalizePhone(guest?.phone);
      const nic = this.normalizeDoc(guest?.nic_number);
      const passport = this.normalizeDoc(guest?.passport_number);

      if (phone) {
        const list = await this.fetchGuestsByQuery(phone);
        const match = list.find(g => this.normalizePhone(g?.phone) === phone);
        if (match) return { match, field: "Phone", value: guest?.phone };
      }

      if (nic) {
        const list = await this.fetchGuestsByQuery(nic);
        const match = list.find(g => this.normalizeDoc(g?.nic_number) === nic);
        if (match) return { match, field: "NIC", value: guest?.nic_number };
      }

      if (passport) {
        const list = await this.fetchGuestsByQuery(passport);
        const match = list.find(
          g => this.normalizeDoc(g?.passport_number) === passport
        );
        if (match) return { match, field: "Passport", value: guest?.passport_number };
      }

      return null;
    },

    async CreateCustomer() {
      this.forceShowCustomerErrors = true;
      this.validateCustomerFields();
      if (this.hasCustomerErrors(this.fieldErrors)) return;

      this.creatingCustomer = true;
      try {
        const guestdata = { ...(this.Guest || {}) };

        const dup = await this.findDuplicateGuest(guestdata);
        if (dup?.match) {
          if (dup.field === "Phone") this.fieldErrors.phone = "Phone number already exists";
          if (dup.field === "NIC") this.fieldErrors.nic_number = "NIC number already exists";
          if (dup.field === "Passport")
            this.fieldErrors.passport_number = "Passport number already exists";

          if (dup.field === "Phone") this.duplicateInfo.phone = dup.match;
          if (dup.field === "NIC") this.duplicateInfo.nic_number = dup.match;
          if (dup.field === "Passport") this.duplicateInfo.passport_number = dup.match;

          this.forceShowCustomerErrors = true;
          await this.SearchCustomer(dup.value);
          this.SelectCustomer(dup.match);
          this.activeTab = "list";
          return;
        }

        await axios.post(`hms/guests`, guestdata);
        await this.SearchCustomer(this.customerName);
        this.Guest = {
          name: "",
          passport_number: "",
          nic_number: "",
          address: "",
          phone: "",
          email: "",
        };
        this.forceShowCustomerErrors = false;
        this.fieldErrors = {
          name: "",
          phone: "",
          nic_number: "",
          passport_number: "",
        };
        this.duplicateInfo = {
          phone: null,
          nic_number: null,
          passport_number: null,
        };
        this.touched = {
          name: false,
          phone: false,
          nic_number: false,
          passport_number: false,
        };
        this.activeTab = "list";
      } catch (err) {
        this.fieldErrors = {
          ...this.fieldErrors,
          phone: this.fieldErrors.phone || "Unable to create customer. Please try again.",
        };
      } finally {
        this.creatingCustomer = false;
      }
    },

    SearchCustomer(query) {
      let url = `hms/web/guests/search?`;
      const q = query != null ? query : this.customerName;
      if (q != null && String(q).trim() !== "") {
        url = url + "query=" + encodeURIComponent(String(q));
      }

      axios
        .get(url)
        .then(res => {
          this.Customers = Array.isArray(res.data?.data) ? res.data.data : [];
          this.totalCustomers = res.data?.meta?.totalItems || this.Customers.length;
        })
        .catch(() => {
          this.Customers = [];
        });
    },

    SelectCustomer(customer) {
      customer.selected = true;
      this.$emit("SelectCustomer", customer);
    },
  },
  created() {
    this.SearchCustomer();
  },
  beforeDestroy() {
    if (this._searchDebounceTimer) clearTimeout(this._searchDebounceTimer);
  },
};
</script>

<style lang="scss" scoped>
$brand: var(--brand-primary);
$brand-dark: var(--brand-primary-hover);
$border: #e2e8f0;
$text: #1e293b;
$muted: #64748b;

.customer-panel {
  width: 100%;
  padding: 4px 0 12px;
  text-align: left;
  box-sizing: border-box;
  overflow: visible;
}

.customer-panel__inner {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  overflow: visible;
}

.customer-panel__search {
  position: relative;
  margin-bottom: 14px;
  overflow: visible;
}

.customer-panel__search > .icon_search {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: $brand;
  font-size: 18px;
  line-height: 1;
  pointer-events: none;
  z-index: 1;
}

.customer-panel__search-input {
  width: 100%;
  height: 44px;
  padding: 0 48px 0 14px;
  border: 1px solid $border;
  border-radius: 10px;
  font-size: 14px;
  color: $text;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  -webkit-appearance: none;
  appearance: none;
}

.customer-panel__search-input:focus {
  outline: none;
  border-color: $brand;
  box-shadow: 0 0 0 3px rgba(74, 45, 133, 0.12);
}

.customer-panel__search-input::placeholder {
  color: #94a3b8;
}

.customer-panel__tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 10px;
}

.customer-panel__tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $muted;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.customer-panel__tab--active {
  background: #fff;
  color: $brand;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
}

.customer-panel__body--list {
  max-height: min(50vh, 420px);
  overflow-y: auto;
  padding-right: 2px;
}

.customer-panel__body--form {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: 4px 20px 24px;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  box-sizing: border-box;
}

.customer-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.customer-card {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 82px;
  gap: 10px 12px;
  align-items: center;
  width: 100%;
  max-width: 488px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
}

.customer-card:hover {
  border-color: #d4c4f0;
  box-shadow: 0 4px 12px rgba(74, 45, 133, 0.1);
}

.customer-card__avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ede9fe 0%, #e0e7ff 100%);
  color: $brand;
  font-size: 16px;
  align-self: center;
}

.customer-card__info {
  min-width: 0;
  text-align: left;
}

.customer-card__name {
  margin: 0 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: $text;
  line-height: 1.25;
  text-align: left;
}

.customer-card__meta {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin: 0 0 3px;
  font-size: 11px;
  color: $muted;
  line-height: 1.35;
  text-align: left;
}

.customer-card__meta:last-child {
  margin-bottom: 0;
}

.customer-card__meta i {
  font-size: 11px;
  color: #94a3b8;
}

.customer-card__select {
  flex-shrink: 0;
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  min-height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, $brand 0%, $brand-dark 100%);
  color: #fff !important;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;
}

.customer-card__select:hover {
  opacity: 0.92;
}

.customer-panel__empty {
  margin: 24px auto;
  max-width: 488px;
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: $muted;
  background: #f8fafc;
  border: 1px dashed $border;
  border-radius: 12px;
}

.customer-form {
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.customer-form__section {
  margin-bottom: 14px;
  padding: 14px;
  background: #fff;
  border: 1px solid $border;
  border-radius: 12px;
}

.customer-form__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 700;
  color: $brand;
}

.customer-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 14px;
}

.customer-form__field--full {
  grid-column: 1 / -1;
}

.customer-form__label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: $muted;
}

.customer-form__input {
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1px solid $border;
  border-radius: 10px;
  font-size: 14px;
  color: $text;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.customer-form__input:focus {
  outline: none;
  border-color: $brand;
  box-shadow: 0 0 0 3px rgba(74, 45, 133, 0.12);
}

.customer-form__input--error {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.12) !important;
}

.customer-form__error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #dc3545;
}

.customer-form__hint {
  margin: 4px 0 0;
  font-size: 11px;
  line-height: 1.35;
}

.customer-form__hint--warn {
  color: #b45309;
}

.customer-form__actions {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}

.customer-form__submit {
  min-width: 160px;
  height: 44px;
  padding: 0 20px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, $brand 0%, $brand-dark 100%);
  color: #fff !important;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.customer-form__submit:hover:not(:disabled) {
  opacity: 0.92;
}

.customer-form__submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .customer-panel__inner {
    max-width: 100%;
    padding: 0 12px;
  }

  .customer-form__grid {
    grid-template-columns: 1fr;
  }

  .customer-card {
    max-width: 100%;
    grid-template-columns: 36px minmax(0, 1fr) 76px;
  }
}
</style>
